import React, { memo, useMemo, Fragment } from 'react';
import { isEmpty, map } from 'lodash-es';
import PropTypes from 'prop-types';
/* Utils */
import { getGlobalSearchLinks } from 'site-modules/shared/utils/inventory/global-search-links';
/* Constants */
import {
  LINK_COMPONENTS,
  GLOBAL_SEARCH_LINKS_COMPONENTS_MAP,
} from 'site-modules/shared/constants/global-search/global-search';
import {
  GlobalSearchLinkDefaultProps,
  GlobalSearchLinkPropTypes,
} from 'site-modules/shared/constants/global-search/global-search-prop-types';
/* Components */
import { SpeculationRule } from 'site-modules/shared/components/speculation-rule/speculation-rule';

const PRERENDER_LINKS_NUM = 1;

export const GlobalSearchLinks = memo(
  ({
    fastMatcherData = GlobalSearchLinkDefaultProps.fastMatcherData,
    query = GlobalSearchLinkDefaultProps.query,
    withLlmSearchDisabled = false,
    creativeId = GlobalSearchLinkDefaultProps.creativeId,
    isMobile = GlobalSearchLinkDefaultProps.isMobile,
    onLinkClick = GlobalSearchLinkDefaultProps.onLinkClick,
    url: urlProp = GlobalSearchLinkDefaultProps.url,
    selectionType = GlobalSearchLinkDefaultProps.selectionType,
    title: titleProp = GlobalSearchLinkDefaultProps.title,
    rel = GlobalSearchLinkDefaultProps.rel,
    linkType: linkTypeProp = GlobalSearchLinkDefaultProps.linkType,
    ...restProps
  }) => {
    const globalSearchLinks = useMemo(
      () => getGlobalSearchLinks({ fastMatcherData, query, withLlmSearchDisabled }),
      [fastMatcherData, query, withLlmSearchDisabled]
    );

    const pricingReviewsLinks = globalSearchLinks.pricingReviewsLinks?.links;
    const speculationLinks = useMemo(
      () => ({
        prerender: pricingReviewsLinks?.slice(0, PRERENDER_LINKS_NUM)?.map(({ url }) => url),
        prefetch: pricingReviewsLinks?.slice(PRERENDER_LINKS_NUM)?.map(({ url }) => url),
      }),
      [pricingReviewsLinks]
    );

    return (
      <Fragment>
        <ul className="list-unstyled mb-0">
          {map(
            globalSearchLinks,
            ({ links, icon, label }) =>
              !isEmpty(links) &&
              links.map(linkConfig => {
                const { title, url, linkType = LINK_COMPONENTS.BASE } = linkConfig;
                const Component = GLOBAL_SEARCH_LINKS_COMPONENTS_MAP[linkType]?.component;
                return (
                  <li key={`${title}-${url}`}>
                    <Component
                      {...restProps}
                      fastMatcherData={fastMatcherData}
                      withLlmSearchDisabled={withLlmSearchDisabled}
                      creativeId={creativeId}
                      isMobile={isMobile}
                      onLinkClick={onLinkClick}
                      url={urlProp}
                      selectionType={selectionType}
                      title={titleProp}
                      rel={rel}
                      linkType={linkTypeProp}
                      query={query}
                      icon={icon}
                      label={label}
                      {...linkConfig}
                    />
                  </li>
                );
              })
          )}
        </ul>
        {!!speculationLinks.prerender?.length && (
          <SpeculationRule speculationTimeout={750} urls={speculationLinks.prerender} />
        )}
        {!!speculationLinks.prefetch?.length && (
          <SpeculationRule action="prefetch" speculationTimeout={1250} urls={speculationLinks.prefetch} />
        )}
      </Fragment>
    );
  }
);

GlobalSearchLinks.displayName = 'GlobalSearchLinks';

GlobalSearchLinks.propTypes = {
  ...GlobalSearchLinkPropTypes,
  withLlmSearchDisabled: PropTypes.bool,
};
